import React, { useState, useEffect } from 'react';
import { facilityApi, memberApi, stripeConnectApi } from '../../api';
import { SubscriptionPlan } from '../../api_types';
import { FacilityMember } from '../../api_types';
import { UserCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

interface FacilityMembersProps {
  facilityId: string;
  businessId: string;
}

const getInitials = (firstName: string, lastName: string) => {
  if (!firstName && !lastName) return '';
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
};

const FacilityMembers: React.FC<FacilityMembersProps> = ({ facilityId, businessId }) => {
  const [members, setMembers] = useState<FacilityMember[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<FacilityMember[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    subscriptionStatus: '',
    planId: '',
    sortBy: 'name' as 'name' | 'joinDate' | 'subscriptionStatus'
  });
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await facilityApi.getFacilityMembers(businessId, facilityId);
      setMembers(data);
      setFilteredMembers(data);
    } catch (err) {
      setError('Failed to fetch members');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [facilityId]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { plans: facilityPlans } = await stripeConnectApi.getPlans(businessId, facilityId);
        setPlans(facilityPlans);
      } catch (err) {
        console.error('Error fetching plans:', err);
      }
    };
    fetchPlans();
  }, [facilityId]);

  useEffect(() => {
    let result = [...members];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(member => 
        member.firstName.toLowerCase().includes(searchLower) ||
        member.lastName.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower)
      );
    }

    // Apply subscription status filter
    if (filters.subscriptionStatus) {
      result = result.filter(member => 
        member.subscriptionStatus === filters.subscriptionStatus
      );
    }

    // Apply plan filter
    if (filters.planId) {
      result = result.filter(member => 
        member.planId === filters.planId
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (filters.sortBy) {
        case 'name':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'joinDate':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'subscriptionStatus':
          return a.subscriptionStatus.localeCompare(b.subscriptionStatus);
        default:
          return 0;
      }
    });

    setFilteredMembers(result);
  }, [members, filters]);

  const getUniqueValues = (field: 'subscriptionStatus') => {
    return Array.from(new Set(members.map(member => member[field]))) as string[];
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this member? This will cancel their subscription, remove their access, and delete all their data.')) {
      return;
    }

    try {
      setRemovingMemberId(memberId);
      await memberApi.deleteMember(memberId, businessId, facilityId);
      setMembers(prevMembers => prevMembers.filter(member => member.id !== memberId));
    } catch (err) {
      setError('Failed to delete member');
      console.error('Error deleting member:', err);
    } finally {
      setRemovingMemberId(null);
    }
  };

  return (
    <div className="bg-white shadow-xl rounded-2xl p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <UserCircleIcon className="h-7 w-7 text-indigo-500" /> Facility Members
        </h2>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search members..."
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            value={filters.subscriptionStatus}
            onChange={(e) => setFilters(prev => ({ ...prev, subscriptionStatus: e.target.value }))}
          >
            <option value="">All Statuses</option>
            {getUniqueValues('subscriptionStatus').map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            value={filters.planId}
            onChange={(e) => setFilters(prev => ({ ...prev, planId: e.target.value }))}
          >
            <option value="">All Plans</option>
            {plans.map(plan => (
              <option key={plan.priceId} value={plan.priceId}>{plan.name}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            value={filters.sortBy}
            onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value as typeof filters.sortBy }))}
          >
            <option value="name">Sort by Name</option>
            <option value="joinDate">Sort by Join Date</option>
            <option value="subscriptionStatus">Sort by Status</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : filteredMembers.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[300px]">
          <UserCircleIcon className="h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No members found</h3>
          <p className="text-gray-500 text-sm">Invite your first member to get started!</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-100">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Subscription</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Join Date</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredMembers.map((member, idx) => {
                const planName = member.planId
                  ? plans.find((plan) => plan.priceId === member.planId)?.name || 'N/A'
                  : 'N/A';
                return (
                  <tr key={member.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-indigo-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex items-center gap-3">
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs">
                        {getInitials(member.firstName, member.lastName)}
                      </span>
                      {member.firstName} {member.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{member.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{member.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{planName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        member.subscriptionStatus === 'active'
                          ? 'bg-green-100 text-green-800'
                          : member.subscriptionStatus === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {member.subscriptionStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(member.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={removingMemberId === member.id}
                        className={`text-red-600 hover:text-red-900 ${
                          removingMemberId === member.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {removingMemberId === member.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FacilityMembers; 